import { useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { useUserStore } from '../stores/userStore';
import { useLanguage } from '../hooks/useLanguage';

export function WalletPage() {
  const { user, stats, updateWallet } = useUserStore();
  const { t } = useLanguage();
  const [walletInput, setWalletInput] = useState(user?.wallet_address || '');
  const [isUpdating, setIsUpdating] = useState(false);

  const balance = stats ? (stats.balance_usd / 100).toFixed(2) : '0.00';
  const minWithdraw = 5.0;
  const canWithdraw = stats && stats.balance_usd >= minWithdraw * 100;

  const handleUpdateWallet = async () => {
    if (!walletInput || !/^0x[a-fA-F0-9]{40}$/.test(walletInput)) {
      WebApp.showAlert('Please enter a valid ETH address (0x...)');
      return;
    }

    setIsUpdating(true);
    try {
      await updateWallet(walletInput);
      WebApp.showAlert('Wallet updated successfully');
    } catch (error) {
      WebApp.showAlert('Failed to update wallet');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleWithdraw = () => {
    if (!canWithdraw) {
      WebApp.showAlert(`Minimum withdrawal is $${minWithdraw.toFixed(2)}`);
      return;
    }

    if (!user?.wallet_address) {
      WebApp.showAlert('Please set your wallet address first');
      return;
    }

    WebApp.showConfirm(
      `Withdraw $${balance} to ${user.wallet_address.slice(0, 6)}...${user.wallet_address.slice(-4)}?`,
      (confirmed) => {
        if (confirmed) {
          WebApp.showAlert('Withdrawal request submitted. Processing...');
        }
      }
    );
  };

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-bold text-tg">{t('wallet')}</h1>

      {/* Balance Card */}
      <div className="bg-brand-primary rounded-xl p-6 text-white">
        <p className="text-sm opacity-80">{t('available_balance')}</p>
        <p className="text-4xl font-bold mt-1">${balance}</p>
        <p className="text-sm opacity-80 mt-2">
          {t('min_withdrawal')}: ${minWithdraw.toFixed(2)}
        </p>
      </div>

      {/* Wallet Address */}
      <div className="bg-tg rounded-xl p-5 space-y-4">
        <h2 className="font-semibold text-tg">{t('eth_wallet_address')}</h2>
        <p className="text-sm text-tg-hint">
          {t('eth_wallet_desc')}
        </p>
        <input
          type="text"
          value={walletInput}
          onChange={(e) => setWalletInput(e.target.value)}
          placeholder="0x..."
          className="w-full px-4 py-3 bg-tg-secondary rounded-xl text-tg font-mono text-sm"
        />
        <button
          onClick={handleUpdateWallet}
          disabled={isUpdating || walletInput === user?.wallet_address}
          className="w-full py-3 rounded-xl bg-tg-secondary text-tg font-medium disabled:opacity-50"
        >
          {isUpdating ? t('updating') : t('update_wallet')}
        </button>
      </div>

      {/* Withdraw Button */}
      <button
        onClick={handleWithdraw}
        disabled={!canWithdraw || !user?.wallet_address}
        className="w-full py-4 rounded-xl bg-brand-primary text-white font-medium disabled:opacity-50"
      >
        {!user?.wallet_address
          ? t('set_wallet_to_withdraw')
          : !canWithdraw
          ? t('need_min_to_withdraw', { amount: `$${minWithdraw.toFixed(2)}` })
          : `${t('withdraw')} $${balance}`}
      </button>

      {/* Info */}
      <div className="bg-tg rounded-xl p-5">
        <h2 className="font-semibold text-tg mb-2">{t('how_payouts_work')}</h2>
        <ul className="text-sm text-tg-hint space-y-2">
          <li>{t('payout_step_1')}</li>
          <li>{t('payout_step_2')}</li>
          <li>{t('payout_step_3', { amount: `$${minWithdraw.toFixed(2)}` })}</li>
          <li>{t('payout_step_4')}</li>
        </ul>
      </div>
    </div>
  );
}
